import { projectsProjectAccessDecisionSchema, } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema, } from '@crest/module-projects/contracts/project-reference';
import { z } from 'zod';
export const insuranceClaimsClaimsCapability = 'insurance-claims.claims.v1';
export const insuranceClaimsClaimVersionsCapability = 'insurance-claims.claim-versions.v1';
export const insuranceClaimsIdSchema = z.string().uuid();
export const insuranceClaimsMoneyCentsSchema = z.number().int().min(0).max(999_999_999_999);
export const insuranceClaimsStatusSchema = z.enum(['open', 'closed', 'cancelled']);
export const insuranceClaimsPerilTypeSchema = z.enum([
    'water', 'fire', 'mold', 'storm', 'biohazard', 'other',
]);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
export const insuranceClaimsCarrierSnapshotSchema = z.strictObject({
    displayName: z.string().trim().min(1).max(240),
    relationshipOrganizationId: z.string().uuid().nullable(),
    phone: nullableText(80),
    email: z.string().trim().email().max(320).nullable(),
});
export const insuranceClaimsAdjusterSnapshotSchema = z.strictObject({
    displayName: z.string().trim().min(1).max(240),
    relationshipContactId: z.string().uuid().nullable(),
    phone: nullableText(80),
    email: z.string().trim().email().max(320).nullable(),
});
const insuranceClaimsDraftShape = {
    projectId: insuranceClaimsIdSchema,
    claimNumber: nullableText(120),
    policyNumber: nullableText(120),
    carrier: insuranceClaimsCarrierSnapshotSchema.nullable(),
    adjuster: insuranceClaimsAdjusterSnapshotSchema.nullable(),
    tpaProgram: nullableText(240),
    perilType: insuranceClaimsPerilTypeSchema,
    causeOfLoss: nullableText(2_000),
    lossDate: z.string().date().nullable(),
    dateOfDiscovery: z.string().date().nullable(),
    isWaterLoss: z.boolean(),
    waterCategory: z.number().int().min(1).max(3).nullable(),
    waterClass: z.number().int().min(1).max(4).nullable(),
    moldCondition: z.number().int().min(1).max(3).nullable(),
    deductibleCents: insuranceClaimsMoneyCentsSchema.nullable(),
    notes: nullableText(8_000),
};
function validateWaterFacts(claim, context) {
    if (!claim.isWaterLoss && (claim.waterCategory !== null || claim.waterClass !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['isWaterLoss'],
            message: 'Water Category and Class require a water-loss claim.',
        });
    }
    if (claim.perilType === 'water' && !claim.isWaterLoss) {
        context.addIssue({
            code: 'custom',
            path: ['isWaterLoss'],
            message: 'A water peril must be marked as a water loss.',
        });
    }
}
export const insuranceClaimsDraftSchema = z.strictObject(insuranceClaimsDraftShape).superRefine(validateWaterFacts);
const { projectId: _projectIdSchema, ...insuranceClaimsVersionDataShape } = insuranceClaimsDraftShape;
export const insuranceClaimsVersionDataSchema = z.strictObject({
    ...insuranceClaimsVersionDataShape,
    projectReference: projectsProjectReferenceSnapshotSchema,
    projectAccessDecision: projectsProjectAccessDecisionSchema,
}).superRefine(validateWaterFacts);
export const insuranceClaimsCommandReasonSchema = z.string().trim().min(10).max(500);
export const insuranceClaimsCreateClaimCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    claim: insuranceClaimsDraftSchema,
    reason: insuranceClaimsCommandReasonSchema,
});
export const insuranceClaimsReviseClaimCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    commandId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    claim: insuranceClaimsDraftSchema,
    reason: insuranceClaimsCommandReasonSchema,
});
export const insuranceClaimsTransitionClaimCommandSchema = z.strictObject({
    kind: z.literal('transition'),
    commandId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    targetStatus: insuranceClaimsStatusSchema,
    reason: insuranceClaimsCommandReasonSchema,
});
export const insuranceClaimsClaimStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']),
    commandId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: insuranceClaimsCommandReasonSchema,
});
export const insuranceClaimsClaimCommandSchema = z.discriminatedUnion('kind', [
    insuranceClaimsReviseClaimCommandSchema,
    insuranceClaimsTransitionClaimCommandSchema,
    insuranceClaimsClaimStateCommandSchema,
]);
export const insuranceClaimsClaimVersionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsClaimVersionsCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    versionNumber: z.number().int().positive(),
    status: insuranceClaimsStatusSchema,
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: insuranceClaimsCommandReasonSchema,
    actorMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    data: insuranceClaimsVersionDataSchema,
});
export const insuranceClaimsClaimSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsClaimsCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    status: insuranceClaimsStatusSchema,
    revision: z.number().int().positive(),
    currentVersionId: insuranceClaimsIdSchema,
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: insuranceClaimsIdSchema,
    updatedByMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: insuranceClaimsClaimVersionSchema,
});
export const insuranceClaimsClaimListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    status: insuranceClaimsStatusSchema.nullable().default(null),
    projectId: insuranceClaimsIdSchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const insuranceClaimsPageSchema = z.strictObject({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
export const insuranceClaimsCommandReceiptSchema = z.strictObject({
    commandId: insuranceClaimsIdSchema,
    idempotent: z.boolean(),
});
export const insuranceClaimsClaimResponseSchema = z.strictObject({
    data: insuranceClaimsClaimSchema,
    command: insuranceClaimsCommandReceiptSchema.optional(),
});
export const insuranceClaimsClaimsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsClaimSchema),
    page: insuranceClaimsPageSchema,
});
export const insuranceClaimsClaimVersionsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsClaimVersionSchema),
});
//# sourceMappingURL=insurance-claims-claim.contract.js.map